export type DateBucket = { key: string; label: string };

type Translator = (key: any) => any;

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
] as const;

export function getDateBucket(
  timestamp: number | string | undefined,
  t: Translator = (s) => s,
  nowMs: number = Date.now(),
): DateBucket {
  const ts = Number(timestamp);
  if (!ts || Number.isNaN(ts)) {
    return { key: "unknown", label: String(t("Earlier")) };
  }

  const itemDate = new Date(ts);
  const now = new Date(nowMs);

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
  const startOfYear = new Date(now.getFullYear(), 0, 1).getTime();

  if (ts >= startOfToday) return { key: "today", label: String(t("Today")) };
  if (ts >= startOfYesterday) return { key: "yesterday", label: String(t("Yesterday")) };
  if (ts >= startOfMonth) return { key: "earlier-this-month", label: String(t("Earlier this Month")) };
  if (ts >= startOfPrevMonth) {
    const prevMonthIdx = (now.getMonth() + 11) % 12;
    const monthName = MONTH_NAMES[prevMonthIdx] ?? "";
    return { key: `month-${prevMonthIdx}`, label: String(t(monthName)) };
  }
  if (ts >= startOfYear) return { key: "earlier-this-year", label: String(t("Earlier this Year")) };

  const year = itemDate.getFullYear();
  return { key: `year-${year}`, label: String(year) };
}

export type BucketRange = { fromDate?: number; toDate?: number };

// Inverse of getDateBucket: given a bucket key, return the [fromDate, toDate)
// timestamp range (ms) that bucket covers. Used to mark a whole group read on
// the backend — including notifications not yet loaded by pagination. An open
// upper bound (e.g. "today") omits toDate.
export function getBucketRange(key: string, nowMs: number = Date.now()): BucketRange {
  const now = new Date(nowMs);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const startOfPrevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1).getTime();
  const startOfYear = new Date(now.getFullYear(), 0, 1).getTime();

  switch (key) {
    case "today":
      return { fromDate: startOfToday };
    case "yesterday":
      return { fromDate: startOfYesterday, toDate: startOfToday };
    case "earlier-this-month":
      return { fromDate: startOfMonth, toDate: startOfYesterday };
    case "earlier-this-year":
      return { fromDate: startOfYear, toDate: startOfPrevMonth };
    default:
      break;
  }

  // The single "previous calendar month" bucket (key is `month-<index>`).
  if (key.startsWith("month-")) {
    return { fromDate: startOfPrevMonth, toDate: startOfMonth };
  }

  // A specific past calendar year (key is `year-<YYYY>`).
  if (key.startsWith("year-")) {
    const year = Number(key.slice("year-".length));
    if (!Number.isNaN(year)) {
      return { fromDate: new Date(year, 0, 1).getTime(), toDate: new Date(year + 1, 0, 1).getTime() };
    }
  }

  return {};
}

export type BucketGroup<T> = { bucket: DateBucket; items: T[] };

export function groupByBucket<T extends { createdDate?: number | string }>(
  items: T[],
  t: Translator = (s) => s,
  nowMs: number = Date.now(),
): BucketGroup<T>[] {
  const groups: BucketGroup<T>[] = [];
  let current: BucketGroup<T> | null = null;
  for (const item of items) {
    const bucket = getDateBucket(item.createdDate, t, nowMs);
    if (!current || current.bucket.key !== bucket.key) {
      current = { bucket, items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }
  return groups;
}
